import { useEffect, useRef, useState, useCallback } from 'react';

interface VitalSigns {
  hr: number;
  bp_sys: number;
  bp_dia: number;
  spo2: number;
  rr: number;
  temp: number;
}

interface MonitorAudioOptions {
  vitals: VitalSigns | null;
  isVisible: boolean;
  hasAlert: boolean;
}

/**
 * Custom hook that synthesizes realistic hospital monitor sounds using the Web Audio API.
 * - Heart rate beep: short 880Hz sine tone synced to the patient's HR
 * - Alarm: alternating 440/880Hz rapid beep pattern when vitals are critical
 */
export function useMonitorAudio({ vitals, isVisible, hasAlert }: MonitorAudioOptions) {
  const [isMuted, setIsMuted] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const alarmIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const alarmToggleRef = useRef(false);

  // Lazily create AudioContext (must be triggered after user gesture - 
  // the monitor is shown after the user has already interacted with the page)
  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    // Resume if suspended (browser autoplay policy)
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  // Play a short beep tone
  const playBeep = useCallback((frequency: number, duration: number, volume: number = 0.15) => {
    if (isMuted) return;
    try {
      const ctx = getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

      // Quick attack, sustain, then decay — sounds like a real monitor beep
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.005);
      gainNode.gain.setValueAtTime(volume, ctx.currentTime + duration * 0.6);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      // Silently fail — audio is not critical
      console.warn('[MonitorAudio] Beep failed:', e);
    }
  }, [isMuted, getAudioContext]);

  // Cleanup all intervals and audio nodes
  const stopAll = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (alarmIntervalRef.current) {
      clearInterval(alarmIntervalRef.current);
      alarmIntervalRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.suspend().catch(() => { });
    }
  }, []);

  // Heart rate beep — synced to HR value
  useEffect(() => {
    // Clear previous interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (!vitals || !isVisible || isMuted) return;

    // Calculate interval from HR: e.g. HR 60 = 1000ms, HR 120 = 500ms
    const intervalMs = Math.max(300, Math.min(2000, 60000 / vitals.hr));

    // Play immediately, then on interval
    playBeep(880, 0.08, 0.12);

    heartbeatIntervalRef.current = setInterval(() => {
      playBeep(880, 0.08, 0.12);
    }, intervalMs);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [vitals?.hr, isVisible, isMuted, playBeep]);

  // Alarm beep — rapid alternating tones when vitals are critical
  useEffect(() => {
    if (alarmIntervalRef.current) {
      clearInterval(alarmIntervalRef.current);
      alarmIntervalRef.current = null;
    }

    if (!hasAlert || !isVisible || isMuted) return;

    alarmIntervalRef.current = setInterval(() => {
      alarmToggleRef.current = !alarmToggleRef.current;
      const freq = alarmToggleRef.current ? 660 : 440;
      playBeep(freq, 0.15, 0.2);
    }, 350);

    return () => {
      if (alarmIntervalRef.current) {
        clearInterval(alarmIntervalRef.current);
        alarmIntervalRef.current = null;
      }
    };
  }, [hasAlert, isVisible, isMuted, playBeep]);

  // Force stop everything if not visible
  useEffect(() => {
    if (!isVisible) {
      stopAll();
    }
  }, [isVisible, stopAll]);

  // Cleanup AudioContext on unmount
  useEffect(() => {
    return () => {
      stopAll();
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => { });
        audioCtxRef.current = null;
      }
    };
  }, [stopAll]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  return { isMuted, toggleMute };
}

